import { FC, useEffect, useRef } from 'react';

import { close } from '@purpur/library/icon/assets/close';
import { endUser } from '@purpur/library/icon/assets/end-user';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useBrowserModalControls } from '@telialt/common-hooks/src/useBrowserModalControls';
import { isSSR } from '@telialt/utils/src/helpers';
import { useResolution } from '@telialt/utils/src/hooks';
import { isDesktopRes } from '@telialt/utils/src/responsive';

import { UserMenuItemType } from '../../model';
import { NAVIGATION_TEST_IDS } from '../../test-ids';
import { MenuDrawer } from '../MenuDrawer/MenuDrawer';
import { MenuItemIcon } from '../MenuItem/MenuItem.styles';
import { MobileMenuWrapper } from '../MobileMenu/MobileMenu.styles';
import { MainMenuContainer, UserButton, UserIconWrapper, UserMenuContainer } from './MenuItemUser.styles';
import { UserMenuListItem } from './UserMenuListItem';

export const UserMenuMobile: FC<{ menuItems: UserMenuItemType[] }> = ({ menuItems }) => {
    const { t } = useTranslations();
    const { isModalOpen, openModal, closeModal } = useBrowserModalControls('navigation-mobile-menu-user');
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const isDesktop = useResolution(() => !isSSR() && isDesktopRes());

    const menuId = 'mobile-menu-user';

    useEffect(() => {
        if (isDesktop && isModalOpen) {
            closeModal();
        }
    }, [closeModal, isDesktop, isModalOpen]);

    const handleToggleDrawer = () => {
        if (isModalOpen) {
            closeModal();
        } else {
            openModal();
        }
    };

    return (
        <UserMenuContainer data-test={NAVIGATION_TEST_IDS.USER}>
            <UserButton
                ref={menuButtonRef}
                onClick={handleToggleDrawer}
                aria-label={t['navbar.user_menu.aria_label']}
                aria-haspopup="true"
                aria-controls={menuId}
                aria-expanded={isModalOpen}
                $isActive={isModalOpen}
                $displayOption="Mobile only"
            >
                <UserIconWrapper>
                    <MenuItemIcon svg={isModalOpen ? close : endUser} size="sm" />
                </UserIconWrapper>
                {isModalOpen ? t['common.button.close'] : t['navbar.user.authorized']}
            </UserButton>
            <MenuDrawer id={menuId} buttonRef={menuButtonRef} isVisible={isModalOpen} onClose={() => closeModal()}>
                <MobileMenuWrapper role="menu" aria-label={t['navbar.user_menu.aria_label']}>
                    <MainMenuContainer>
                        {menuItems.map((item) => (
                            <UserMenuListItem key={item.id} menuItem={item} displayOption="Mobile only" />
                        ))}
                    </MainMenuContainer>
                </MobileMenuWrapper>
            </MenuDrawer>
        </UserMenuContainer>
    );
};
